import { getPath } from './planState';

export const field = (label, path, input = 'text', extra = {}) => ({
  type: 'field',
  label,
  path,
  input,
  ...extra,
});

export const grid = (cols, children, extra = {}) => ({ type: 'grid', cols, children, ...extra });

export const chips = (label, path, items, extra = {}) => ({
  type: 'chips',
  label,
  path,
  items,
  ...extra,
});

export const card = (title, children, extra = {}) => ({
  type: 'card',
  title,
  children,
  ...extra,
});

export function careTeamFields(program, extraFields = []) {
  if (program === 'PCM') {
    return [
      field('PCM Care Managers', 'team.caregiverIds', 'multiselect', {
        required: true,
        optionSource: 'practiceCaregivers',
        namePath: 'team.careManager',
        signoffNamePath: 'signoff.managerName',
        placeholder: 'Select caregivers',
      }),
      field('PCM provider', 'team.pcp', 'select', {
        required: true,
        optionSource: 'practiceProviders',
        placeholder: 'Select provider',
      }),
      ...extraFields,
    ];
  }
  return [
    field(`${program} care manager`, 'team.careManager', 'text', { required: true }),
    field('Primary care provider', 'team.pcp', 'text'),
    ...extraFields,
  ];
}

const fromPatient = { disabled: true };

function insuranceTierFields(prefix) {
  return [
    field('Insurance name', `${prefix}.insuranceName`, 'text', fromPatient),
    field('Subscriber ID', `${prefix}.subscriberId`, 'text', fromPatient),
    field("Insured's name", `${prefix}.insuredName`, 'text', fromPatient),
    field('Group no.', `${prefix}.groupNo`, 'text', fromPatient),
  ];
}

function hasSecondaryInsurance(plan) {
  const slot = plan?.patient?.insuranceSecondary || {};
  return ['insuranceName', 'subscriberId', 'insuredName', 'groupNo']
    .some((key) => String(slot[key] || '').trim());
}

export function insuranceCard() {
  return card('Insurance', [
    { type: 'heading', text: 'Primary' },
    grid(4, insuranceTierFields('patient.insurancePrimary')),
    { type: 'heading', text: 'Secondary', showWhen: hasSecondaryInsurance },
    grid(4, insuranceTierFields('patient.insuranceSecondary'), { showWhen: hasSecondaryInsurance }),
  ], { subtitle: 'Coverage on file for this patient.' });
}

export function patientInformationChildren(program) {
  const fields = [
    field('First name', 'patient.firstName', 'text', { ...fromPatient, placeholder: 'e.g., John' }),
    field('Last name', 'patient.lastName', 'text', { ...fromPatient, placeholder: 'e.g., Doe' }),
    field('Gender', 'patient.sex', 'select', { ...fromPatient, options: ['Female', 'Male', 'Other'] }),
    field('Date of birth', 'patient.dob', 'date', fromPatient),
    field('Phone number', 'patient.phone', 'text', { ...fromPatient, placeholder: '(000) 000-0000' }),
    field('MRN / Chart #', 'patient.mrn', 'text', { ...fromPatient, placeholder: 'e.g. 004821' }),
    field('Emergency contact number', 'patient.emergencyPhone', 'text', { ...fromPatient, placeholder: '(000) 000-0000' }),
    field('Preferred language', 'patient.language', 'text', { placeholder: 'English' }),
  ];
  if (program !== 'PCM') {
    fields.push(field('Primary insurance', 'patient.insurance', 'text', fromPatient));
  }
  return [
    grid(3, fields),
    grid(3, [
      field('Care plan start date', 'patient.startDate', 'date', { required: true }),
    ], { className: 'acp-grid--split' }),
  ];
}

export function patientAndTeamCards(program) {
  return [
    card('Patient information', patientInformationChildren(program)),
    ...(program === 'PCM' ? [insuranceCard()] : []),
    card('Care team', [
      grid(2, careTeamFields(program, [
        field('Specialist', 'team.specialist', 'text'),
        field('Caregiver / family contact', 'team.caregiver', 'text'),
      ])),
    ], { subtitle: 'Everyone with a named responsibility in this plan.' }),
  ];
}

export function goalsCard(outcomeItems, interventionItems) {
  return [
    card('Expected outcomes', [
      chips('Outcomes', 'outcomes', outcomeItems, { stack: true }),
    ], { collapsible: true }),
    card('Measurable goals', [
      {
        type: 'rows', path: 'goals', addLabel: 'Add goal', columns: [
          { key: 'goal', label: 'Goal', placeholder: 'Goal' },
          { key: 'target', label: 'Target', placeholder: 'Measurable target' },
          { key: 'date', label: 'Target date', input: 'date' },
        ]
      },
    ], { collapsible: true, subtitle: 'Each goal needs a target and a date to be auditable.' }),
    card('Planned interventions & follow-up', [
      chips('Interventions', 'interventions', interventionItems),
    ], { collapsible: true }),
  ];
}

export function medicationListCard(extraChildren = []) {
  return card('Medication list', [
    ...extraChildren,
    { type: 'meds', path: 'meds.list' },
    {
      type: 'attests', items: [
        { path: 'meds.reconciled', label: 'Reconciliation completed' },
        { path: 'meds.renalReviewed', label: 'Renal / hepatic dosing reviewed' },
        { path: 'meds.interactionsChecked', label: 'Interacting drugs screened' },
      ]
    },
  ]);
}

export function monitorCard(items, subtitle) {
  return card('Monitoring plan', [
    { type: 'monitors', path: 'monitoring', items },
  ], { collapsible: true, subtitle: subtitle || 'Set a frequency for every parameter this patient is being followed on.' });
}

export function emergencyCards(readout) {
  return [
    card('Call 911 immediately for', [
      { type: 'readout', text: readout },
    ]),
    card('Patient-specific thresholds', [
      grid(2, [
        field('Call the care team if a key measure is below', 'emergency.low', 'text'),
        field('Call the care team if a key measure is above', 'emergency.high', 'text'),
      ]),
      field('Patient-specific action steps', 'emergency.actions', 'textarea', {
        required: true,
        rows: 4,
        placeholder: 'What this patient should do, in order, when symptoms start — including who to call and when.',
      }),
      field('Household trained in emergency response', 'emergency.cprTrained', 'select', {
        options: ['Yes', 'No', 'Referral made'],
      }),
    ]),
  ];
}

export function educationCards(topics, lifestyle, resources) {
  return [
    card('Education provided', [
      chips('Topics', 'education.topics', topics),
      {
        type: 'attests', items: [
          { path: 'education.teachBack', label: 'Teach-back completed' },
          { path: 'education.materials', label: 'Written materials given' },
          { path: 'education.caregiver', label: 'Caregiver included' },
        ]
      },
    ], { collapsible: true }),
    card('Lifestyle goals', [
      chips('Lifestyle', 'education.lifestyle', lifestyle),
    ], { collapsible: true }),
    card('Community & social resources', [
      field('Social or functional needs identified', 'resources.needs', 'textarea', {
        rows: 2,
        placeholder: 'Transportation, cost, caregiver capacity, housing stability',
      }),
      chips('Resources arranged', 'resources.arranged', resources),
    ], { collapsible: true }),
  ];
}

export function signoffCards(program) {
  const agreementStaff = program === 'PCM'
    ? [
      field('Care Managers', 'team.caregiverIds', 'multiselect', {
        required: true,
        optionSource: 'practiceCaregivers',
        namePath: 'team.careManager',
        signoffNamePath: 'signoff.managerName',
        placeholder: 'Select caregivers',
      }),
      field('Supervising provider', 'team.pcp', 'select', {
        required: true,
        optionSource: 'practiceProviders',
        placeholder: 'Select provider',
      }),
    ]
    : [
      field('Care manager name & credentials', 'signoff.managerName', 'text', {
        required: true,
        placeholder: 'e.g. J. Alvarez, RN',
      }),
      field('Supervising provider', 'signoff.providerName', 'text'),
    ];
  return [
    card('Review schedule', [
      grid(2, [
        field('Review interval', 'signoff.reviewInterval', 'select', {
          options: ['Monthly', 'Quarterly', 'Every 6 months', 'Annually'],
        }),
        field('Next review date', 'signoff.nextReview', 'date', { required: true }),
      ]),
    ]),
    card('Patient agreement', [
      { type: 'readout', text: 'The patient participated in developing this care plan, received a copy, and consents to chronic care management services and the coordination of care among their providers.' },
      {
        type: 'attests', items: [
          { path: 'signoff.patientSigned', label: 'Patient agreement obtained' },
        ]
      },
      grid(2, [
        field('Date of patient agreement', 'signoff.patientDate', 'date'),
        field('Date signed by care manager', 'signoff.managerDate', 'date'),
        ...agreementStaff,
      ]),
    ], { roomy: true }),
  ];
}

export function basePlan(patientSeedValues) {
  return {
    patient: {
      name: patientSeedValues.name || '',
      firstName: patientSeedValues.firstName || '',
      lastName: patientSeedValues.lastName || '',
      dob: patientSeedValues.dob || '',
      mrn: patientSeedValues.mrn || '',
      sex: patientSeedValues.sex || '',
      phone: patientSeedValues.phone || '',
      language: '',
      emergencyContact: patientSeedValues.emergencyContact || '',
      emergencyFirstName: patientSeedValues.emergencyFirstName || '',
      emergencyLastName: patientSeedValues.emergencyLastName || '',
      emergencyRelation: patientSeedValues.emergencyRelation || '',
      emergencyPhone: patientSeedValues.emergencyPhone || '',
      insurance: patientSeedValues.insurance || '',
      insurancePrimary: patientSeedValues.insurancePrimary || { insuranceName: '', subscriberId: '', insuredName: '', groupNo: '' },
      insuranceSecondary: patientSeedValues.insuranceSecondary || { insuranceName: '', subscriberId: '', insuredName: '', groupNo: '' },
      startDate: '',
    },
    team: { careManager: '', caregiverIds: [], pcp: '', pcpUserId: '', specialist: '', caregiver: '' },
    goals: [
      { id: 'g1', goal: '', target: '', date: '' },
    ],
    meds: { list: [], reconciled: false, renalReviewed: false, interactionsChecked: false },
    monitoring: {},
    emergency: { low: '', high: '', actions: '', cprTrained: '' },
    interventions: [],
    outcomes: [],
    education: { topics: [], lifestyle: [], teachBack: false, materials: false, caregiver: false },
    resources: { needs: '', arranged: [] },
    signoff: {
      patientSigned: false,
      patientDate: '',
      managerName: '',
      managerDate: '',
      providerName: '',
      providerDate: '',
      reviewInterval: 'Annually',
      nextReview: '',
    },
  };
}

export const FREQUENCIES = ['Daily', 'Twice daily', 'Weekly', 'Monthly', 'Quarterly', 'Every 6 months', 'Annually', 'As needed'];

function isBlank(value) {
  if (value == null) return true;
  if (typeof value === 'string') return value.trim() === '';
  if (Array.isArray(value)) return value.length === 0;
  return false;
}

export function collectMissingRequired(nodes, plan, ctx, missing = []) {
  (nodes || []).forEach((node) => {
    if (!node || typeof node !== 'object') return;
    if (typeof node.showWhen === 'function' && !node.showWhen(plan, ctx)) return;
    if (node.type === 'field' && !node.disabled) {
      const required = node.required || (typeof node.requiredWhen === 'function' && node.requiredWhen(plan, ctx));
      if (required && node.path && isBlank(getPath(plan, node.path))) {
        missing.push({ path: node.path, label: node.label || 'Required field' });
      }
    }
    if (Array.isArray(node.children)) collectMissingRequired(node.children, plan, ctx, missing);
  });
  return missing;
}

export function countDone(map, total) {
  const done = Object.keys(map).reduce((sum, key) => sum + (map[key] ? 1 : 0), 0);
  return { map, pct: total ? Math.round((done / total) * 100) : 0 };
}
